import React from 'react';
import { motion } from 'framer-motion';
import { 
  Briefcase, 
  GraduationCap, 
  Heart, 
  Users, 
  TrendingUp,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertCircle,
  Star
} from 'lucide-react';
import { useGameStore } from '../store/gameStore';

const StatusPanel: React.FC = () => {
  const { status, money, stats } = useGameStore();

  const getStatusIcon = (status: boolean) => {
    return status ? (
      <CheckCircle className="text-emerald-400" size={16} />
    ) : (
      <XCircle className="text-red-400" size={16} />
    );
  };

  const getHealthStatus = () => {
    if (stats.health >= 80) return { text: 'Excellent', color: 'text-emerald-400', icon: '💚' };
    if (stats.health >= 60) return { text: 'Good', color: 'text-amber-400', icon: '💛' };
    if (stats.health >= 40) return { text: 'Fair', color: 'text-orange-400', icon: '🧡' };
    return { text: 'Poor', color: 'text-red-400', icon: '❤️' };
  };

  const getJobStatus = () => {
    if (!status.hasJob) return { text: 'Unemployed', color: 'text-red-400', icon: '🚫' };
    if (stats.wealth >= 80) return { text: 'Executive', color: 'text-purple-400', icon: '👔' };
    if (stats.wealth >= 60) return { text: 'Manager', color: 'text-blue-400', icon: '💼' };
    if (stats.wealth >= 40) return { text: 'Professional', color: 'text-green-400', icon: '👨‍💼' };
    if (stats.wealth >= 20) return { text: 'Entry Level', color: 'text-amber-400', icon: '👷' };
    return { text: 'Part-time', color: 'text-slate-400', icon: '🕐' };
  };

  const getEducationStatus = () => {
    if (!status.isEducating) return { text: 'Not Studying', color: 'text-slate-400', icon: '📚' };
    if (stats.education >= 80) return { text: 'PhD Level', color: 'text-purple-400', icon: '🎓' };
    if (stats.education >= 60) return { text: 'Masters', color: 'text-blue-400', icon: '🎓' };
    if (stats.education >= 40) return { text: 'Bachelors', color: 'text-green-400', icon: '🎓' };
    return { text: 'Certificate', color: 'text-amber-400', icon: '📜' };
  };

  const getFamilyStatus = () => {
    if (!status.hasFamily) return { text: 'Single', color: 'text-slate-400', icon: '👤' };
    if (status.familyHappiness >= 80) return { text: 'Very Happy', color: 'text-emerald-400', icon: '😊' };
    if (status.familyHappiness >= 60) return { text: 'Happy', color: 'text-green-400', icon: '🙂' };
    if (status.familyHappiness >= 40) return { text: 'Neutral', color: 'text-amber-400', icon: '😐' };
    return { text: 'Unhappy', color: 'text-red-400', icon: '😞' };
  };

  const getInvestmentStatus = () => {
    if (!status.isInvesting) return { text: 'Not Investing', color: 'text-slate-400', icon: '📉' };
    if (stats.wealth >= 80) return { text: 'High Risk', color: 'text-red-400', icon: '📈' };
    if (stats.wealth >= 60) return { text: 'Moderate', color: 'text-amber-400', icon: '📊' };
    return { text: 'Conservative', color: 'text-green-400', icon: '💰' };
  };

  const healthStatus = getHealthStatus();
  const jobStatus = getJobStatus();
  const educationStatus = getEducationStatus();
  const familyStatus = getFamilyStatus();
  const investmentStatus = getInvestmentStatus();

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50">
      <h3 className="text-xl font-bold text-white mb-6 flex items-center">
        <Star className="mr-2" />
        Life Status
      </h3>
      
      <div className="space-y-4">
        {/* Money Display */}
        <div className="bg-gradient-to-r from-emerald-500/20 to-green-500/20 border border-emerald-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <DollarSign className="text-emerald-400" size={20} />
              <span className="text-emerald-400 font-medium">Available Money</span>
            </div>
            <span className="text-2xl font-bold text-emerald-400">
              ${money.toLocaleString()}
            </span>
          </div>
          <div className="text-sm text-emerald-300 mt-1">
            Monthly Income: ${status.monthlyIncome.toLocaleString()}
          </div>
          <div className="text-sm text-slate-300 mt-1">
            Monthly Expenses: ${status.monthlyExpenses.toLocaleString()}
          </div>
          <div className={`text-sm font-bold mt-1 ${
            status.netIncome >= 0 ? 'text-emerald-400' : 'text-red-400'
          }`}>
            Net Income: {status.netIncome >= 0 ? '+' : ''}${status.netIncome.toLocaleString()}
          </div>
        </div>

        {/* Job Status */}
        <div className="bg-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <Briefcase className="text-blue-400" size={18} />
              <span className="text-white font-medium">Employment</span>
            </div>
            {getStatusIcon(status.hasJob)}
          </div>
          <div className={`text-sm ${jobStatus.color} flex items-center space-x-2`}>
            <span className="text-lg">{jobStatus.icon}</span>
            <span>{jobStatus.text}</span>
          </div>
          {status.jobTitle && (
            <div className="text-xs text-slate-400 mt-1">{status.jobTitle}</div>
          )}
        </div>

        {/* Education Status */}
        <div className="bg-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <GraduationCap className="text-purple-400" size={18} />
              <span className="text-white font-medium">Education</span>
            </div>
            {getStatusIcon(status.isEducating)}
          </div>
          <div className={`text-sm ${educationStatus.color} flex items-center space-x-2`}>
            <span className="text-lg">{educationStatus.icon}</span>
            <span>{educationStatus.text}</span>
          </div>
          {status.educationLevel && (
            <div className="text-xs text-slate-400 mt-1">{status.educationLevel}</div>
          )}
        </div>

        {/* Family Status */}
        <div className="bg-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <Users className="text-pink-400" size={18} />
              <span className="text-white font-medium">Family</span>
            </div>
            {getStatusIcon(status.hasFamily)}
          </div>
          <div className={`text-sm ${familyStatus.color} flex items-center space-x-2`}>
            <span className="text-lg">{familyStatus.icon}</span>
            <span>{familyStatus.text}</span>
          </div>
          {status.hasFamily && (
            <div className="text-xs text-slate-400 mt-1">
              Happiness: {status.familyHappiness}/100
            </div>
          )}
        </div>

        {/* Health Status */}
        <div className="bg-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <Heart className="text-red-400" size={18} />
              <span className="text-white font-medium">Health</span>
            </div>
            {getStatusIcon(status.isHealthy)}
          </div>
          <div className={`text-sm ${healthStatus.color} flex items-center space-x-2`}>
            <span className="text-lg">{healthStatus.icon}</span>
            <span>{healthStatus.text}</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Score: {stats.health}/100
          </div>
        </div>

        {/* Investment Status */}
        <div className="bg-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <TrendingUp className="text-yellow-400" size={18} />
              <span className="text-white font-medium">Investments</span>
            </div>
            {getStatusIcon(status.isInvesting)}
          </div>
          <div className={`text-sm ${investmentStatus.color} flex items-center space-x-2`}>
            <span className="text-lg">{investmentStatus.icon}</span>
            <span>{investmentStatus.text}</span>
          </div>
          {status.investmentType && (
            <div className="text-xs text-slate-400 mt-1">{status.investmentType}</div>
          )}
        </div>

        {/* Living Location Status */}
        <div className="bg-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">🏠</span>
              <span className="text-white font-medium">Living Location</span>
            </div>
            <CheckCircle className="text-emerald-400" size={16} />
          </div>
          <div className="text-sm text-emerald-400 flex items-center space-x-2">
            <span className="text-lg">📍</span>
            <span>{status.livingLocation}</span>
          </div>
        </div>

        {/* ZUS Insurance Status */}
        <div className="bg-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">🏛️</span>
              <span className="text-white font-medium">ZUS Insurance</span>
            </div>
            {getStatusIcon(status.isZusRegistered)}
          </div>
          <div className={`text-sm ${status.isZusRegistered ? 'text-emerald-400' : 'text-slate-400'} flex items-center space-x-2`}>
            <span className="text-lg">{status.isZusRegistered ? '✅' : '❌'}</span>
            <span>{status.isZusRegistered ? 'Registered' : 'Not Registered'}</span>
          </div>
          {status.isZusRegistered && (
            <div className="text-xs text-slate-400 mt-1">
              Monthly contribution: $200
            </div>
          )}
        </div>

        {/* Pet Status */}
        <div className="bg-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">🐱</span>
              <span className="text-white font-medium">Pet</span>
            </div>
            {getStatusIcon(status.hasPet)}
          </div>
          <div className={`text-sm ${status.hasPet ? 'text-emerald-400' : 'text-slate-400'} flex items-center space-x-2`}>
            <span className="text-lg">{status.hasPet ? '🐱' : '🚫'}</span>
            <span>{status.hasPet ? `${status.petType} - Happy` : 'No Pet'}</span>
          </div>
          {status.hasPet && (
            <div className="text-xs text-slate-400 mt-1">
              Happiness: {status.petHappiness}/100
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatusPanel;
